import React from 'react';
import styles from './MarkdownContent.module.css';
import { MarkdownContentProps } from '../../types/message';

const PATTERNS = {
  BOLD: /\*\*([^*]+)\*\*/g,
  ITALIC: /\*([^*]+)\*/g,
  HIGHLIGHT: /`([^`]+)`/g,
  STRIKETHROUGH: /~([^~]+)~/g,
};

/**
 * Renders inline markdown elements as React components
 */
function renderInlineMarkdown(text: string): React.ReactNode[] {
  let result: React.ReactNode[] = [text];

  // Process each pattern in sequence
  Object.entries(PATTERNS).forEach(([type, pattern]) => {
    result = result.flatMap((node) => {
      if (typeof node !== 'string') return [node];
      
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      
      node.replace(pattern, (match, content, index) => {
        if (index > lastIndex) {
          parts.push(node.slice(lastIndex, index));
        }
        
        switch (type) {
          case 'BOLD':
            parts.push(<strong key={index}>{content}</strong>);
            break;
          case 'ITALIC':
            parts.push(<em key={index}>{content}</em>);
            break;
          case 'HIGHLIGHT':
            parts.push(<code key={index}>{content}</code>);
            break;
          case 'STRIKETHROUGH':
            parts.push(<del key={index}>{content}</del>);
            break;
        }
        
        lastIndex = index + match.length;
        return match;
      });
      
      if (lastIndex < node.length) {
        parts.push(node.slice(lastIndex));
      }
      
      return parts;
    });
  });

  return result;
}

/**
 * A React component that safely renders inline markdown content
 */
export const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, className = '' }) => {
  return (
    <div className={`${styles.markdown} ${className}`}>
      {renderInlineMarkdown(content)}
    </div>
  );
};

export default MarkdownContent; 